import { type FC, type ReactNode, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { useQuery } from "@apollo/client/react";

import {
  AssistantScope,
  type DocumentFragment,
  DocumentKindEnum,
  type IntakeInterviewStatusEnum,
  type ProjectFragment,
  RecordReviewDisplayState,
  RecordReviewStatus,
  RecordReviewWorkflowDocument,
  RunStatusEnum,
  type StepStateEnum,
  type StepStates,
  type TranscriptDocumentFragment,
  type TranscriptStateEnum,
} from "@app_schema";

import { CaseSupportingLink } from "@styled/case_steps";

import { AssistantToggle } from "./assistant/assistant_toggle";
import { LockedRecordsSplit } from "./assistant/locked_records_split";
import {
  type IntakeType,
  SECTION_PARAMS,
  caseSteps,
} from "./case_status_lines";
import { DraftingTab } from "./drafting_tab";
import { FINAL_REPORT_COPY } from "./final_report_pane";
import { Intake } from "./intake";
import { IntakeInterviewPane } from "./intake_interview/intake_interview_pane";
import { ProcessRecordsCard } from "./process_records_card";
import { RecordsReviewSlot } from "./records_review_slot";
import { ReportSection } from "./report_section";
import { SupportingDocumentsPane } from "./supporting_documents_pane";
import { TranscriptsPane } from "./transcripts_pane";
import { useProjectDocumentSave } from "./use_project_document_save";

export const ProjectsDocuments: FC<{
  project: ProjectFragment & {
    recordReviewNewLetterCount?: number;
    recordReviewReviewedLetterCount?: number;
    recordReviewFilteredLetterCount?: number;
    recordReviewBaselineLetterCount?: number;
    recordReviewPageCount?: number;
    recordReviewTotalBillableUnits?: number;
    recordReviewNetBillableUnits?: number;
    recordReviewNonBillableUnits?: number;
    recordReviewFullPageCount?: number;
    recordReviewCondensedPageCount?: number;
    recordReviewCondensedBillableUnits?: number;
    recordReviewCondensedMultiplier?: number;
    recordReviewHasCondensedPages?: boolean;
    recordReviewBilling?: {
      buckets: {
        baseline: number;
        previouslySeen: number;
        duplicate: number;
        nonEssential: number;
        unmatched: number;
      };
      pageCountBuckets: {
        baseline: number;
        previouslySeen: number;
        duplicate: number;
        nonEssential: number;
        unmatched: number;
      };
    } | null;
    documents: DocumentFragment[];
    supportingDocuments: DocumentFragment[];
    reviewRecordDocuments: DocumentFragment[];
    reviewRecordDocumentKind: DocumentKindEnum;
    hasProcessedReviewRecordDocument: boolean;
    recordsReviewReady: boolean;
    finalReportDocuments: DocumentFragment[];
    hasProcessedFinalReport: boolean;
    draftingEnabled: boolean;
    draftingState?: StepStateEnum | null;
    reportDraftDocuments: DocumentFragment[];
    hasProcessedReportDraft: boolean;
    hasDraftTemplate: boolean;
    draftingStartable: boolean;
    draftingReading: boolean;
    intakeInterviewEnabled: boolean;
    intakeInterview?: { status: IntakeInterviewStatusEnum } | null;
    transcriptDocuments: TranscriptDocumentFragment[];
    openingTranscriptDocumentId?: string | null;
    dictationState?: TranscriptStateEnum | null;
    stepStates?: StepStates | null;
    recordReview?: {
      status: RecordReviewStatus;
      displayState: RecordReviewDisplayState;
      additionalRecordsCount: number;
      displayedPagesCount: number;
      pagesCount: number;
      processedPagesCount: number;
      lettersCount: number;
      reprocessable: boolean;
      canChangeEvaluationType: boolean;
      hint: string;
      hintColor: string;
      title?: string | null;
      subtitle?: string | null;
      polling: boolean;
      hasCompletedRun: boolean;
      run?: { id: string; status: RunStatusEnum } | null;
    } | null;
    recordsAgent?: { id: string; name: string } | null;
    reportAgent?: { id: string; name: string } | null;
    draftingAgent?: { id: string; name: string } | null;
  };
  // The single selected section, lifted to the detail page. The section drives
  // BOTH the work area (this pane) and the assistant scope (one source of truth).
  selectedType: IntakeType;
  onSelectType: (key: IntakeType) => void;
  // Opens the page-level eval-type picker from the Records pane's `← Change`.
  onChangeEvalType: () => void;
}> = ({ project, selectedType, onSelectType, onChangeEvalType }) => {
  // The assistant's open state, toggled by the header button (its only close).
  // Until toggled, it opens on Report; Records stays on the cover letter.
  const [searchParams, setSearchParams] = useSearchParams();
  const [assistantChoice, setAssistantChoice] = useState<boolean>();
  // `?section=` (the cases list's status lines and Evaluate report) opens that
  // tab once, then the param is dropped so a refresh behaves like a normal visit.
  useEffect(() => {
    const section = SECTION_PARAMS.get(searchParams.get("section") ?? "");
    if (!section) return;

    onSelectType(section);
    const next = new URLSearchParams(searchParams);
    next.delete("section");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, onSelectType]);

  const { data: workflowData } = useQuery(RecordReviewWorkflowDocument);

  const recordReviewWorkflow = workflowData?.runnableWorkflow;
  const recordReviewRun = project.recordReview?.run;

  // Shared with the guided empty state so both attach through one path.
  const saveDocument = useProjectDocumentSave(project.id);

  const steps = caseSteps(project);

  // The assistant lives only in the sections that have one, and only once
  // the section holds a file: until then the tab's one action is the upload.
  // Its scope is fixed by the selected section.
  const sectionFiles: Partial<Record<IntakeType, DocumentFragment[]>> = {
    records: project.reviewRecordDocuments,
    finalReport: project.finalReportDocuments,
    drafting: project.reportDraftDocuments,
  };
  const hasAssistant =
    (sectionFiles[selectedType]?.length ?? 0) > 0 && project.policy.assist;
  const assistantOpen = assistantChoice ?? selectedType !== "records";

  const assistantToggle = hasAssistant ? (
    <AssistantToggle
      open={assistantOpen}
      onToggle={() => setAssistantChoice(!assistantOpen)}
    />
  ) : null;
  const showAssistant = assistantOpen && hasAssistant;

  // The process/upload card is the whole Records pane until records are
  // processed; once ready it collapses behind the summary strip, above the
  // inline review (see RecordsReviewSlot).
  const processRecordsCard = (
    <ProcessRecordsCard
      project={project}
      documents={project.documents}
      recordReview={project.recordReview}
      workflow={recordReviewWorkflow}
      id="stage-process-records"
      onChangeEvalType={onChangeEvalType}
    />
  );

  const panes: Record<IntakeType, ReactNode> = {
    // Records pane: the upload/process card while records are still coming in;
    // once processed, the Medical Summary strip + collapsed card + the records
    // review embedded inline (the same view as /records-review/:runId).
    records:
      project.recordsReviewReady && recordReviewRun?.id ? (
        <RecordsReviewSlot
          runId={recordReviewRun.id}
          pages={project.recordReviewPageCount ?? 0}
          billable={project.recordReviewNetBillableUnits ?? 0}
          card={processRecordsCard}
          assistant={{
            open: showAssistant,
            projectId: project.id,
            agentId: project.recordsAgent?.id,
          }}
        />
      ) : showAssistant ? (
        <LockedRecordsSplit projectId={project.id}>
          {processRecordsCard}
        </LockedRecordsSplit>
      ) : (
        processRecordsCard
      ),
    finalReport: (
      <ReportSection
        project={project}
        scope={AssistantScope.Report}
        agentId={project.reportAgent?.id}
        documents={project.finalReportDocuments}
        save={(blob) => saveDocument(blob, DocumentKindEnum.FinalReport)}
        available={project.hasProcessedFinalReport}
        copy={FINAL_REPORT_COPY}
        showAssistant={showAssistant}
      />
    ),
    drafting: (
      <DraftingTab
        project={project}
        agentId={project.draftingAgent?.id}
        documents={project.reportDraftDocuments}
        available={project.hasProcessedReportDraft}
        showAssistant={showAssistant}
        save={saveDocument}
      />
    ),
    intakeInterview: <IntakeInterviewPane projectId={project.id} />,
    dictation: (
      <TranscriptsPane
        project={project}
        documents={project.transcriptDocuments}
        openingId={project.openingTranscriptDocumentId}
        // The server makes it audio or notes by the file.
        save={(blob) => saveDocument(blob, DocumentKindEnum.AudioRecording)}
      />
    ),
    supporting: (
      <SupportingDocumentsPane
        project={project}
        documents={project.supportingDocuments}
        save={(blob) => saveDocument(blob, null)}
      />
    ),
  };

  return (
    <Intake
      steps={steps}
      selected={selectedType}
      onSelect={onSelectType}
      supportingLink={
        <CaseSupportingLink
          open={selectedType === "supporting"}
          onClick={() => onSelectType("supporting")}
        >
          Supporting documents
        </CaseSupportingLink>
      }
      assistant={assistantToggle}
    >
      {panes[selectedType]}
    </Intake>
  );
};
